import { CalendarDays, TrendingUp, TrendingDown, Wallet, AlertCircle, Users } from "lucide-react";
import { formatCurrency } from "@/lib/data";

/** Totals for the whole filtered set, as sent by GET /reports/events. */
export interface EventTotals {
  events: number;
  by_status: Record<string, number>;
  guests: number;
  received: number;
  owed: number;
  events_owing: number;
  revenue?: number;
  gst?: number;
  billed?: number;
  cost?: number;
  profit?: number;
  margin?: number;
}

function Tile({ title, value, subtitle, icon: Icon, tone }: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  tone: "blue" | "emerald" | "amber" | "rose" | "violet" | "teal";
}) {
  const tones = {
    blue: "bg-blue-50 text-blue-800 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    rose: "bg-rose-50 text-rose-800 border-rose-100",
    violet: "bg-violet-50 text-violet-800 border-violet-100",
    teal: "bg-teal-50 text-teal-800 border-teal-100",
  };
  return (
    <div className={`p-3 sm:p-4 rounded-xl border min-w-0 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 opacity-70 shrink-0" />
        <p className="text-[11px] font-bold uppercase tracking-wide opacity-80 truncate">{title}</p>
      </div>
      <p className="text-lg sm:text-xl font-bold mt-1 truncate tabular-nums">{value}</p>
      {subtitle && <p className="text-xs mt-0.5 opacity-75">{subtitle}</p>}
    </div>
  );
}

export function QuickStatsCards({ totals, showMoney }: { totals: EventTotals; showMoney: boolean }) {
  const confirmed = (totals.by_status.confirmed ?? 0) + (totals.by_status.in_progress ?? 0) + (totals.by_status.completed ?? 0);
  const cancelled = totals.by_status.cancelled ?? 0;
  const drafts = totals.by_status.draft ?? 0;
  const statusLine = [
    `${confirmed} booked`,
    drafts ? `${drafts} draft` : "",
    cancelled ? `${cancelled} cancelled` : "",
  ].filter(Boolean).join(" · ");
  const profit = totals.profit ?? 0;

  return (
    <div className={`grid grid-cols-2 gap-3 ${showMoney ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
      <Tile title="Events" value={totals.events} subtitle={statusLine} icon={CalendarDays} tone="blue" />
      {showMoney ? (
        <>
          <Tile
            title="Revenue (before GST)"
            value={formatCurrency(totals.revenue ?? 0)}
            subtitle={`+ GST ${formatCurrency(totals.gst ?? 0)} = billed ${formatCurrency(totals.billed ?? 0)}`}
            icon={TrendingUp}
            tone="emerald"
          />
          <Tile
            title={profit >= 0 ? "Profit" : "Loss"}
            value={formatCurrency(Math.abs(profit))}
            subtitle={`Cost ${formatCurrency(totals.cost ?? 0)} · ${(totals.margin ?? 0).toFixed(1)}% margin`}
            icon={profit >= 0 ? TrendingUp : TrendingDown}
            tone={profit >= 0 ? "violet" : "rose"}
          />
        </>
      ) : (
        <Tile title="Guests" value={totals.guests.toLocaleString("en-IN")} subtitle="not counting cancelled" icon={Users} tone="emerald" />
      )}
      <Tile title="Received" value={formatCurrency(totals.received)} subtitle="paid by clients so far" icon={Wallet} tone="teal" />
      <Tile
        title="Still owed"
        value={formatCurrency(totals.owed)}
        subtitle={`${totals.events_owing} event${totals.events_owing === 1 ? "" : "s"} with a balance`}
        icon={AlertCircle}
        tone={totals.owed > 0 ? "amber" : "teal"}
      />
    </div>
  );
}
